import { useMutation, type ApolloError } from '@apollo/client'
import { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { CART_ADD_PRODUCT, type IAddProductParams, type ICartAddProduct } from '@commerce/core'

export interface UseUpdateCartQuantity {
  /**
   * Resolves when the mutation settles — even on failure, because `onError`
   * keeps Apollo from rejecting. Callers that only fire-and-forget can ignore
   * it; `useDebouncedCartQuantity` awaits it to know when its optimistic
   * quantity has become the cart's.
   */
  updateQuantity: (sku: string, quantity: number) => Promise<unknown>
  error: ApolloError | undefined
  loading: boolean
}

/**
 * Sets a cart line to a quantity.
 *
 * `replace: true` is the whole point and the plan's sketch omits it.
 * `cartAddProduct` **adds** by default, but `QuantityStepper` commits the new
 * absolute quantity for the line — so without `replace` three clicks of `+` on a
 * line of 3 would send 4, 5, 6 and leave the shopper with 18 units. The field
 * exists on `CartProductOpInput` for exactly this.
 *
 * Quantity 0 is a removal, and the backend treats it as one; the cart page does
 * not special-case it.
 */
export const useUpdateCartQuantity = (): UseUpdateCartQuantity => {
  const { slug = '', sessionId = '' } = useParams()
  const [mutate, { error, loading }] = useMutation<ICartAddProduct, IAddProductParams>(
    CART_ADD_PRODUCT,
    { onError: () => undefined },
  )

  const updateQuantity = useCallback(
    (sku: string, quantity: number) =>
      mutate({
        variables: {
          cartAddProductSessionUid: sessionId,
          cartAddProductStorefrontName: slug,
          cartAddProductData: { sku, quantity, replace: true },
        },
      }),
    [mutate, slug, sessionId],
  )

  return { updateQuantity, error, loading }
}
